import type {
  BulkCardUpdateReq,
  CardUpsertRequest,
  DeleteTerminalCardRequest,
  UpsertTerminalCardRequest,
} from '@api/useTerminalCards/request'
import type { MetadataResponse, TerminalCardOverviewResponse } from '@api/useTerminalCards/response'
import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'

const endpoints = {
  overview: 'cards/terminal/overview',
  metadata: 'cards/metadata',
  upsert: 'cards/terminal/upsert',
  delete: 'cards/terminal/remove',
  bulkAdd: 'cards/terminal/bulk_add',
  update: 'cards/terminal/update',
  bulkUpdate: 'cards/terminal/bulk_update',
} as const

export const useTerminalCards = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const getTerminalCardsOverviewQuery = () =>
    useQuery({
      queryKey: [endpoints.overview],
      queryFn: () => api.post(endpoints.overview) as TerminalCardOverviewResponse[],
    })

  const getCardsMetadataQuery = () =>
    useQuery({ queryKey: [endpoints.metadata], queryFn: () => api.post(endpoints.metadata) as MetadataResponse[] })

  const upsertTerminalCardMutation = useMutation({
    mutationKey: [endpoints.upsert],
    mutationFn: (request: UpsertTerminalCardRequest) => api.post(endpoints.upsert, request),
    onMutate: async (request) => {
      const oldData = queryClient.getQueryData<TerminalCardOverviewResponse[]>([endpoints.overview])
      queryClient.setQueriesData<TerminalCardOverviewResponse[]>({ queryKey: [endpoints.overview] }, (oldData) => {
        return oldData.map((row) => {
          if (row.terminal_id === request.terminal_id && row.card_id === request.card_id) {
            return {
              ...row,
              carding_instructions: request.carding_instructions,
            }
          }
          return row
        })
      })
      return { oldData }
    },
    onError: async (_error, _request, context) => {
      queryClient.setQueriesData({ queryKey: [endpoints.overview] }, context.oldData)
    },
  })

  const deleteTerminalCardMutation = useMutation({
    mutationKey: [endpoints.delete],
    mutationFn: (request: DeleteTerminalCardRequest) => api.post(endpoints.delete, request),
    onMutate: async (request) => {
      const oldData = queryClient.getQueryData<TerminalCardOverviewResponse[]>([endpoints.overview])
      queryClient.setQueriesData<TerminalCardOverviewResponse[]>({ queryKey: [endpoints.overview] }, (oldData) => {
        return oldData.filter((row) => row.id !== request.card_id)
      })
      return { oldData }
    },
    onError: async (_error, _request, context) => {
      queryClient.setQueriesData({ queryKey: [endpoints.overview] }, context.oldData)
    },
  })

  const editTerminalAssignment = useMutation({
    mutationKey: [endpoints.bulkAdd],
    mutationFn: (request: { terminal_ids: string[]; card_id: string }) => api.post(endpoints.bulkAdd, request),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [endpoints.overview],
      })
    },
  })

  const updateCardMutation = useMutation({
    mutationKey: [endpoints.update],
    mutationFn: (request: CardUpsertRequest) => api.post(endpoints.update, request),
    onError: async (_error, _request, context) => {
      await queryClient.invalidateQueries({
        queryKey: [endpoints.overview],
      })
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [endpoints.overview],
      })
    },
  })

  const useBulkCardUpdate = useMutation({
    mutationKey: [endpoints.bulkUpdate],
    mutationFn: (request: BulkCardUpdateReq) => api.post(endpoints.bulkUpdate, request),
    onSettled: async (data: any, error, request, _context) => {
      queryClient.setQueriesData<TerminalCardOverviewResponse[]>({ queryKey: [endpoints.overview] }, (oldData) => {
        return oldData.map((row) => {
          if (request.card_ids.includes(row.id)) {
            const newRow = data?.rows?.find((dataRow) => row.id === dataRow.id)
            return newRow || row
          }
          return row
        })
      })
    },
  })

  return {
    getTerminalCardsOverviewQuery,
    getCardsMetadataQuery,
    upsertTerminalCardMutation,
    deleteTerminalCardMutation,
    editTerminalAssignment,
    updateCardMutation,
    useBulkCardUpdate,
  }
}
